import React, { useEffect, useState, useContext } from "react";
import { NotificationManager } from "react-notifications";
import Select from "react-select";
import Switch from "react-switch";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faSearch } from "@fortawesome/free-solid-svg-icons";
import { FilterContext } from "../FilterContextProvider";
import Input from "../Components/Input";
import useApi from "../Api/Api";
import Button from "../Components/Button";
import AddProduct from "../Forms/AddProduct";
import UploadProduct from "../Forms/UploadProduct";
import InventoryLayout from "./InventoryLayout";
import useProducts from "../hooks/useProducts";
import useAllProducts from "../hooks/useAllProducts";
import { UserContext } from "../Auth/UserContextProvider";
import { exportToCSV } from "../Utility/Csv";
import cn from "classnames";
import CleaveInput from "../Components/CleaveInput";

const ManageInventory = () => {
  const { isAdmin } = useContext(UserContext);
  const { filterData } = useContext(FilterContext);
  const api = useApi();
  const [categories, setCategories] = useState([]);
  const [showManualForm, setShowManualForm] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);
  const [alterProdsObj, setAlterProdObj] = useState({});
  const [allActive, setAllActive] = useState(false);

  const {
    allProducts,
    allProdsActiveFilter,
    setAllProdsActiveFilter,
    setAllProdsCategoryFilter,
    setAllProdsSearchFilter,
  } = useAllProducts("");

  const {
    getProducts,
    products,
    pagy,
    currentPage,
    setCurrentPage,
    activeFilter,
    setActiveFilter,
    categoryFilter,
    setCategoryFilter,
    resetPagination,
    searchFilter,
    setSearchFilter,
  } = useProducts(false);

  const categoryOptions = categories.map((category) => ({
    label: category.name,
    value: category.id,
  }));

  const handleShowManualForm = () => {
    setShowManualForm(!showManualForm);
    setShowUploadForm(false);
  };

  const handleShowUploadForm = () => {
    setShowUploadForm(!showUploadForm);
    setShowManualForm(false);
  };

  const handlePageChange = (e) => {
    setCurrentPage(e.target.value);
  };

  useEffect(() => {
    api.get(`/categories`).then((r) => {
      setCategories(r.data);
    });
  }, [api]);

  const handleInputChange = ({ target: { name, value } }, productToRender) => {
    setAlterProdObj({
      ...alterProdsObj,
      [productToRender.id]: {
        ...productToRender,
        [name]: value,
      },
    });
  };

  const handleCategoryChange = (option, productToRender) => {
    setAlterProdObj({
      ...alterProdsObj,
      [productToRender.id]: {
        ...productToRender,
        category_id: option.value,
      },
    });
  };

  const handleActiveChange = (productToRender) => {
    setAlterProdObj({
      ...alterProdsObj,
      [productToRender.id]: {
        ...productToRender,
        active: !productToRender.active,
      },
    });
  };

  const selectCell = (e) => {
    e.target.select();
  };

  const handleUpdateProducts = (e) => {
    e.preventDefault();
    Promise.all(
      Object.values(alterProdsObj).map((product) => {
        if (!parseFloat(product.retail_price) > 0) {
          product.retail_price = 0;
        } else if (product.retail_price.toString().includes(",")) {
          product.retail_price = parseFloat(
            product.retail_price.replace(/,/g, "")
          );
        }
        if (!parseFloat(product.cost) > 0) {
          product.cost = 0;
        } else if (product.cost.toString().includes(",")) {
          product.cost = parseFloat(product.cost.replace(/,/g, ""));
        }
        return api.put(`/products/${product.id}`, { product });
      })
    )
      .then((response) => {
        NotificationManager.success(
          "Products successfully updated",
          "Success",
          2000
        );
        getProducts(currentPage);
        setAlterProdObj({});
        return response;
      })
      .catch((error) => {
        setErrorMessage(error.response.data);
        if (error.response.data.errors.name) {
          NotificationManager.error(
            "There is already a product for this artist with this name and category. Change the category if you need another copy in the system.",
            "Failure",
            5000
          );
        }
      })
      .finally(() => {});
  };

  const revert = () => {
    const check = window.confirm(
      "Are you sure you want to revert? All unsaved changes will be lost."
    );
    if (check) {
      setAlterProdObj({});
    }
  };

  const deleteProduct = (e, productToDelete) => {
    e.preventDefault();
    const check = window.confirm(
      `Are you sure you want to delete ${productToDelete.name}?`
    );
    if (check) {
      api.delete(`products/${productToDelete.id}`).then(() => {
        getProducts(currentPage);
      });
    }
  };

  const afterSubmit = () => {
    getProducts(currentPage);
  };

  const [searchTimeout, setSearchTimeout] = useState();

  const handleSearchChange = (e) => {
    setSearchTimeout(e.target.value);
    setCurrentPage(1);
  };

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      setSearchFilter(searchTimeout);
      setAllProdsSearchFilter(searchTimeout);
    }, 500);

    return () => clearTimeout(delaySearch);
  }, [searchTimeout, setSearchFilter, setAllProdsSearchFilter]);

  const handleFilterCategory = (option) => {
    resetPagination();
    setCategoryFilter(option.value);
    setAllProdsCategoryFilter(option.value);
  };

  const handleFilterActive = () => {
    resetPagination();
    setActiveFilter(!activeFilter);
    setAllProdsActiveFilter(!allProdsActiveFilter);
  };

  const reset = () => {
    resetPagination();
    setSearchTimeout();
    setCategoryFilter();
    setAllProdsCategoryFilter();
    setActiveFilter(false);
    setAllProdsActiveFilter(false);
  };

  allProducts.forEach((prod) => {
    if (prod.size) {
      prod.size_name = `${prod.size.index}-${prod.size?.name}`;
    } else if (prod.size_other) {
      prod.size_name = `9-${prod.size_other}`;
    }
    if (prod.category) {
      prod.category_name = prod.category?.name;
    }
  });

  const prodsToExport = allProducts.map((product) => ({
    Name: product.name,
    Price: product.retail_price,
    Cost: product.cost,
    Active: product.active,
    Size: product.size?.name,
    Category: product.category?.name,
  }));

  let count = 0;

  for (let i = 1; i < products.length; i++) {
    if (products[i - 1].name !== products[i].name) {
      count++;
    }
    products[i].productNumber = count;
  }

  const activateAll = () => {
    setAllActive(!allActive);
    const obj = {};
    products.forEach((each) => (each.active = !allActive));
    allProducts.forEach((each) => (each.active = !allActive));
    allProducts.forEach((each) => (obj[each.id] = each));
    setAlterProdObj(obj);
  };

  const columns = [
    { label: "PRODUCT", value: "name" },
    { label: "SIZE", value: "size" },
    { label: "RETAIL PRICE", value: "retail_price" },
    { label: "COST", value: "cost" },
    { label: "CATEGORY", value: "category" },
    { label: "ACTIVE", value: "active" },
    { label: "ACTION", value: "action" },
  ];
  
  const renderSelectArtistMessage = () => {
    return (
      <div className="m-8 py-12 pl-4">
        Please select an artist from the filter in order to see products
      </div>
    );
  };

  const renderManageTable = () => {
    return (
      <div>
        <div className="mx-12 flex justify-end text-sm pt-10">
          {!showManualForm &&
            !showUploadForm &&
            Object.keys(alterProdsObj).length === 0 && (
              <>
                <Button
                  onClick={handleShowManualForm}
                  className="button"
                  text="+ New Product"
                ></Button>
                {allProducts.length > 0 && (
                  <div className="ml-2">
                    <Button
                      className="button button-white"
                      text="Export"
                      onClick={() =>
                        exportToCSV(
                          prodsToExport,
                          `${filterData.artist_name}_product_list`
                        )
                      }
                    ></Button>
                  </div>
                )}
              </>
            )}
          {Object.keys(alterProdsObj).length !== 0 && (
            <span>
              <span className="mr-2">
                <Button
                  className="button button-white"
                  onClick={revert}
                  text="Revert"
                ></Button>
              </span>
              <Button
                className="button"
                onClick={handleUpdateProducts}
                text="Update"
              ></Button>
            </span>
          )}
        </div>
        <div className="mx-10">
          <div
            className={
              showManualForm || showUploadForm
                ? " -mt-1 pb-6 font-medium text-sm text-black"
                : " -mt-11 pb-6 font-medium text-sm text-black"
            }
          >
            <div className="text-black mb-2 text-lg font-medium pb-2">
              Full Product List
            </div>
          </div>
          <div className="mb-4 -mt-4 text-sm">
            <div className="flex">
              <div className="absolute mt-2 ml-2 text-black">
                <FontAwesomeIcon icon={faSearch}></FontAwesomeIcon>
              </div>
              <div className="w-1/5">
                <Input
                  onChange={handleSearchChange}
                  value={searchTimeout}
                  placeholder="Search for products"
                  bigPaddingLeft
                />
              </div>
              <Select
                id="category_filter"
                className="text-xs font-light w-1/5 ml-4"
                placeholder="Filter by category"
                options={categoryOptions}
                onChange={handleFilterCategory}
                value={categoryOptions?.filter(
                  (each) => each.value === categoryFilter
                )}
              />
              <label htmlFor="active_filter" className="ml-4 mt-2">
                Filter by active
              </label>
              <Switch
                onChange={handleFilterActive}
                checked={activeFilter}
                height={22}
                width={40}
                onColor="#3b82f6"
                offColor="#d4d4d8"
                className="ml-2 mt-2"
                id="active_filter"
              />
              {(searchFilter || categoryFilter || activeFilter) && (
                <button
                  onClick={reset}
                  className="text-blue-500 font-normal italic text-sm ml-2 pl-2"
                >
                  Reset
                </button>
              )}
            </div>
            <div className="flex justify-end -mt-10 pt-0.5">
              {allActive ? (
                <label htmlFor="activate_all" className="mt-2">
                  Deactivate all
                </label>
              ) : (
                <label htmlFor="activate_all" className="mt-2">
                  Activate all
                </label>
              )}
              <Switch
                onChange={activateAll}
                checked={allActive}
                height={22}
                width={40}
                onColor="#3b82f6"
                offColor="#d4d4d8"
                className="mx-2 mt-2"
                id="activate_all"
              />
            </div>
          </div>
        </div>
        {products.length > 0 ? (
          <div>
            <form className="flex w-full px-2">
              {columns.map((attribute) => {
                return (
                  <table
                    className={cn({
                      "w-1/3": attribute.value === "name",
                      "w-1/6 px-1": attribute.value === "size",
                      "w-1/6":
                        attribute.value === "retail_price" ||
                        attribute.value === "cost" ||
                        attribute.value === "category",
                      "px-2":
                        attribute.value === "active" ||
                        attribute.value === "action",
                    })}
                    key={attribute.value}
                  >
                    <thead className="text-xs font-light">
                      <tr
                        className={cn("text-left border-b-2 h-11", {
                          "text-center": attribute.value === "size",
                        })}
                      >
                        <td>{attribute.label}</td>
                      </tr>
                    </thead>
                    <tbody>
                      {products.map((product, i) => {
                        const productToRender =
                          alterProdsObj[product.id] || product;
                        return (
                          <tr
                            key={productToRender.id}
                            className={cn("border-b text-xs h-11 text-center", {
                              "bg-yellow-50":
                                alterProdsObj[productToRender.product_id],
                              "bg-gray-100":
                                productToRender.productNumber % 2 === 1,
                            })}
                          >
                            {attribute.value === "name" && (
                              <td>
                                <Input
                                  name={attribute.value}
                                  id={`${attribute.value}_${i}`}
                                  onChange={(e) =>
                                    handleInputChange(e, productToRender)
                                  }
                                  value={productToRender.name}
                                  required={productToRender.isToBeUpdated}
                                  onClick={(e) =>
                                    selectCell(e, productToRender)
                                  }
                                />
                              </td>
                            )}
                            {attribute.value === "size" && (
                              <>
                                {product.size?.name ? (
                                  <td>{product.size.name}</td>
                                ) : (
                                  <td>
                                    {product.size_other && product.size_other}
                                  </td>
                                )}
                              </>
                            )}
                            {(attribute.value === "retail_price" ||
                              attribute.value === "cost") && (
                              <td>
                                <CleaveInput
                                  name={attribute.value}
                                  id={`${attribute.value}_${i}`}
                                  cleaveOptions={{
                                    numeral: true,
                                    numeralPositiveOnly: true,
                                  }}
                                  onChange={(e) =>
                                    handleInputChange(e, productToRender)
                                  }
                                  onClick={(e) =>
                                    selectCell(e, productToRender)
                                  }
                                  value={productToRender[attribute.value]}
                                  inputClassName="w-full text-right"
                                  className="mr-2"
                                  prefix={"$"}
                                  required={productToRender.isToBeUpdated}
                                />
                              </td>
                            )}
                            {attribute.value === "category" && (
                              <Select
                                id={`category_id_${i}`}
                                className="font-light mt-0.5"
                                options={categoryOptions}
                                value={categoryOptions?.filter(
                                  (each) =>
                                    each.value === productToRender.category_id
                                )}
                                onChange={(option) =>
                                  handleCategoryChange(option, productToRender)
                                }
                              />
                            )}
                            {attribute.value === "active" && (
                              <input
                                type="checkbox"
                                id={`active_${i}`}
                                checked={!!productToRender.active}
                                onChange={() => {
                                  handleActiveChange(productToRender);
                                }}
                                className="mt-4"
                              />
                            )}
                            {attribute.value === "action" && isAdmin && (
                              <td className="w-12 text-center pr-2">
                                <button
                                  onClick={(e) =>
                                    deleteProduct(e, productToRender)
                                  }
                                >
                                  <FontAwesomeIcon
                                    icon={faTrash}
                                  ></FontAwesomeIcon>
                                </button>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                );
              })}
            </form>
          </div>
        ) : searchFilter || categoryFilter || activeFilter ? (
          <div className="mx-12 py-16">
            No products match the provided search and filters
          </div>
        ) : (
          <div className="mx-12 py-16">
            No products have been added for this artist yet
          </div>
        )}
      </div>
    );
  };

  return (
    <InventoryLayout>
      <div className="text-black bg-gray-100 pt-2">
        {showManualForm && (
          <AddProduct
            handleShowManualForm={handleShowManualForm}
            handleShowUploadForm={handleShowUploadForm}
            afterSubmit={afterSubmit}
          ></AddProduct>
        )}
        {showUploadForm && (
          <UploadProduct
            handleShowUploadForm={handleShowUploadForm}
            handleShowManualForm={handleShowManualForm}
            afterSubmit={afterSubmit}
          ></UploadProduct>
        )}
        <div className={showManualForm || showUploadForm ? "blur" : ""}>
          <div className="mt-8 mx-8 text-black bg-white rounded-lg border">
            {filterData.artist_id
              ? renderManageTable()
              : renderSelectArtistMessage()}
          </div>
          {filterData.artist_id && (
            <>
              <div className="grid justify-items-center">
                {errorMessage?.error && (
                  <div className="ml-32 mt-4 text-red-500 pb-2">
                    {errorMessage.error}
                  </div>
                )}
              </div>
              <footer className="text-sm mt-4 mx-8 pb-4 flex justify-between">
                <div className="ml-1 mr-8">
                  Showing <span className="font-bold">{pagy.from} </span>to
                  <span className="font-bold"> {pagy.to} </span>of
                  <span className="font-bold"> {pagy.count} </span>results
                </div>
                <div className="mr-8">
                  Page {pagy.page} of {pagy.last}
                </div>
                <div className="flex -mt-2">
                  <span>
                    <Button
                      className={
                        pagy.page === 1
                          ? "button-white-disabled button-sm"
                          : "button button-white button-sm"
                      }
                      onClick={resetPagination}
                      text="First"
                      disabled={pagy.page === 1}
                    ></Button>
                  </span>
                  <span className="mx-1">
                    <Button
                      value={pagy.prev}
                      className={
                        !pagy.prev
                          ? "button-white-disabled button-sm"
                          : "button button-white button-sm"
                      }
                      onClick={handlePageChange}
                      text="Prev"
                      disabled={!pagy.prev}
                    ></Button>
                  </span>
                  <span>
                    <Button
                      value={pagy.next}
                      className={
                        !pagy.next
                          ? "button-white-disabled button-sm"
                          : "button button-white button-sm"
                      }
                      onClick={handlePageChange}
                      text="Next"
                      disabled={!pagy.next}
                    ></Button>
                  </span>
                  <span className="ml-1">
                    <Button
                      value={pagy.last}
                      className={
                        pagy.page === pagy.last
                          ? "button-white-disabled button-sm"
                          : "button button-white button-sm"
                      }
                      onClick={handlePageChange}
                      text="Last"
                      disabled={pagy.page === pagy.last}
                    ></Button>
                  </span>
                </div>
              </footer>
            </>
          )}
        </div>
      </div>
    </InventoryLayout>
  );
};

export default ManageInventory;
